import React, { useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Pause, Play } from "@phosphor-icons/react";
import api from "@/lib/api";

const INPUT = "w-full border border-black px-3 py-2 text-sm bg-white";
const BTN = "inline-flex items-center gap-1 border border-black px-3 py-2 text-[10px] uppercase tracking-widest transition-colors duration-100 disabled:opacity-40";
const POLICIES = [
  ["strict", "Strict (phone + email only)"],
  ["moderate", "Moderate (also last name + ZIP)"],
  ["aggressive", "Aggressive (also sound-alike name + city)"],
];
const pick = (b) => ({
  price: b.price ?? "", return_window_days: b.return_window_days ?? 5,
  replacement_cap: b.replacement_cap ?? "", duplicate_policy: b.duplicate_policy || "moderate",
});
const num = (v) => (v === "" || v === null ? null : Number(v));
const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function Row({ label, hint, children }) {
  return (
    <label className="block">
      <span className="label-mono">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="mt-1 block text-[10px] text-zinc-500">{hint}</span>}
    </label>
  );
}

// Manage Buyer → "Buyer overview": price, return window, replacement cap, duplicate policy and pause/resume.
export function BuyerOverviewEditor({ buyer, orgId, onSaved }) {
  const [form, setForm] = useState(() => pick(buyer));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(buyer));
  const reload = async () => {
    const b = (await api.get(`/organizations/${orgId}`)).data;
    onSaved(b);
    setForm(pick(b));
  };
  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      await reload();
      toast.success(ok);
    } catch (e) {
      toast.error(errText(e, "Couldn't save the overview"));
    } finally { setBusy(false); }
  };
  const save = () => run(() => api.patch(`/organizations/${orgId}`, {
    price: num(form.price), return_window_days: num(form.return_window_days) ?? 5,
    replacement_cap: num(form.replacement_cap), duplicate_policy: form.duplicate_policy,
  }), "Buyer overview saved");
  const paused = buyer.status === "paused";
  const toggle = () => run(() => api.post(`/organizations/${orgId}/${paused ? "resume" : "pause"}`), paused ? "Buyer resumed" : "Buyer paused");
  return (
    <div className="space-y-4" data-testid="buyer-overview-editor">
      <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div><span className="label-mono">Contact email:</span> {buyer.contact_email || "—"}</div>
        <div><span className="label-mono">Contact phone:</span> {buyer.contact_phone || "—"}</div>
        <div className="text-[11px] text-zinc-500 md:col-span-2">Contact email and phone are changed in Edit details above.</div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Row label="Default price ($ per lead)" hint="What this buyer pays per delivered lead.">
          <input type="number" min={0} step="0.01" value={form.price} onChange={set("price")} className={INPUT} data-testid="buyer-overview-price" />
        </Row>
        <Row label="Return window (days)" hint="How long they can return a bad lead.">
          <input type="number" min={0} value={form.return_window_days} onChange={set("return_window_days")} className={INPUT} data-testid="buyer-overview-return-window" />
        </Row>
        <Row label="Replacement cap" hint="Most returned leads replaced. Empty = no limit.">
          <input type="number" min={0} value={form.replacement_cap} onChange={set("replacement_cap")} className={INPUT} data-testid="buyer-overview-replacement-cap" />
        </Row>
        <Row label="Duplicate policy" hint="How hard we look for leads they already got.">
          <select value={form.duplicate_policy} onChange={set("duplicate_policy")} className={INPUT} data-testid="buyer-overview-duplicate-policy">
            {POLICIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={busy || !dirty} data-testid="buyer-overview-save"
                className={`${BTN} bg-black text-white hover:bg-emerald-700`}>
          <FloppyDisk size={12} weight="bold" /> {busy ? "Saving…" : "Save overview"}
        </button>
        <span className="label-mono" data-testid="buyer-overview-status">Status: {buyer.status}</span>
        <button type="button" onClick={toggle} disabled={busy} data-testid="buyer-overview-toggle-status"
                className={`${BTN} ${paused ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white hover:bg-black hover:text-white"}`}>
          {paused ? <Play size={12} weight="bold" /> : <Pause size={12} weight="bold" />} {paused ? "Resume buyer" : "Pause buyer"}
        </button>
      </div>
    </div>
  );
}
